import React, { useEffect } from 'react';
import { useI18n } from '../i18n/i18nContext';
import Hero from '../components/Hero';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useServiceStore } from '../stores/serviceStore';
import LocalizedLink from '../components/LocalizedLink';
import { getTranslatedField } from '../utils/translationUtils';
import NewsletterSubscription from '../components/NewsletterSubscription';
import ContentPageRenderer from '../components/ContentPageRenderer';
import SEO from '../components/SEO';
import CarouselGallery from '../components/CarouselGallery';
import LocalizedButton from 'src/components/Button/LocalizedButton';

const PackagesPage: React.FC = () => {
  const { t, language } = useI18n();
  useDocumentTitle(t.pageTitles.home);
  const { services, loadServices } = useServiceStore();

  useEffect(() => {
    loadServices(language);
  }, [loadServices, language]);

  const contrastServiceSlug = 'contrast-therapy';
  const mainServices = services.filter((s) => s.slug !== contrastServiceSlug);
  const contrastService = services.find((s) => s.slug === contrastServiceSlug);

  return (
    <div className="flex flex-col">
      <SEO description={t.seo.homeDescription} />
      <Hero />

      <ContentPageRenderer slug="home-intro" className="container mx-auto mt-8 max-w-6xl px-4" />
      <section className="pt-6 text-center">
        <LocalizedButton to="/book" name={t.servicePage?.bookNow} size="md" />
      </section>

      <main className="flex-grow">
        <div id="services" className="container mx-auto max-w-6xl px-4 py-16">
          <h2 className="mb-8 text-center text-3xl font-bold">{t.ourServices}</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {mainServices.map((service) => (
              <LocalizedLink
                to={`/services/${service.slug || ''}`}
                key={service.id}
                className="block overflow-hidden rounded-lg bg-white shadow-lg transition-transform duration-300 hover:scale-105"
              >
                <img
                  className="h-48 w-full object-cover"
                  src={service.image || `/images/services/${service.slug}.webp`}
                  alt={getTranslatedField(service, 'name', language)}
                />
                <div className="p-6">
                  <h3 className="mb-2 text-xl font-bold">
                    {getTranslatedField(service, 'name', language)}
                  </h3>
                  <p className="mb-4 text-gray-600">
                    {getTranslatedField(service, 'description', language)}
                  </p>
                  <span className="hidden text-sm font-semibold text-blue-600 hover:underline md:inline">
                    {t.learnMore} &rarr;
                  </span>
                </div>
              </LocalizedLink>
            ))}
          </div>

          {contrastService && (
            <LocalizedLink
              to={`/services/${contrastService.slug || ''}`}
              key={contrastService.id}
              className="mt-8 block overflow-hidden rounded-lg bg-white shadow-lg transition-transform duration-300 hover:scale-105"
            >
              <div className="md:flex md:items-stretch">
                <div className="md:w-48 md:flex-shrink-0">
                  <img
                    className="h-48 w-full object-cover"
                    src={contrastService.image || '/images/services/contrast-therapy.webp'}
                    alt={getTranslatedField(contrastService, 'name', language)}
                  />
                </div>
                <div className="flex flex-col justify-center p-6 md:flex-1">
                  <h3 className="mb-2 text-2xl font-bold">
                    {getTranslatedField(contrastService, 'name', language)}
                  </h3>
                  <p className="mb-4 text-gray-700">
                    {getTranslatedField(contrastService, 'description', language)}
                  </p>
                  <span className="hidden text-sm font-semibold text-blue-600 hover:underline md:inline">
                    {t.learnMore} &rarr;
                  </span>
                </div>
              </div>
            </LocalizedLink>
          )}
        </div>
      </main>

      <CarouselGallery />
      <NewsletterSubscription />
    </div>
  );
};

export default PackagesPage;
